import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, Briefcase, TrendingUp, Activity, DollarSign, Mail, FileText, Handshake, ChevronRight, ChevronDown, Building2, Shield, AlertCircle, BarChart3, Scale, Wrench } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ForeignVendorsAdminPanel from '@/components/admin/ForeignVendorsAdminPanel';
import SupportRequestsAdminPanel from '@/components/admin/SupportRequestsAdminPanel';
import MigrationRollbackAdminPanel from '@/components/admin/MigrationRollbackAdminPanel';
import EmailPreviewAdminPanel from '@/components/admin/EmailPreviewAdminPanel';
import InvoiceDesignerPanel from '@/components/admin/InvoiceDesignerPanel';
import RegulatoryRadarPanel from '@/components/admin/RegulatoryRadarPanel';
import AdminUsagePanel from '@/components/admin/AdminUsagePanel';
import ConversionFunnelPanel from '@/components/admin/ConversionFunnelPanel';
import LexWaitlistPanel from '@/components/admin/LexWaitlistPanel';
import KanzleiTeamPanel from '@/components/admin/KanzleiTeamPanel';
import AdminUserOverviewPanel from '@/components/admin/AdminUserOverviewPanel';
import PlatformBillingPanel from '@/components/admin/PlatformBillingPanel';

const ADMIN_EMAIL = 'wilhelm.wagner.consulting@gmail.com';
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

// Gruppierte Admin-Navigation (Sep 2026) — thematisch sortiert, umbruchfähig
const ADMIN_NAV_GROUPS = [
  {
    label: 'Überblick', icon: BarChart3,
    tabs: [
      { id: 'analytics', label: 'Kennzahlen' },
      { id: 'users', label: 'Nutzer' },
      { id: 'usage', label: 'Aktivität' },
      { id: 'insights', label: 'Conversion' },
      { id: 'ai-usage', label: 'KI-Kosten' },
    ],
  },
  {
    label: 'Wachstum', icon: TrendingUp,
    tabs: [
      { id: 'referrals', label: 'Empfehlungen' },
      { id: 'beta-manager', label: 'Beta-Tester' },
      { id: 'invoice-leads', label: 'Rechnungs-Leads' },
      { id: 'partner-leads', label: 'Partner-Leads' },
    ],
  },
  {
    label: 'Steuerberater', icon: Building2,
    tabs: [
      { id: 'sb-overview', label: 'Übersicht' },
      { id: 'sb-billing', label: 'Abrechnung' },
    ],
  },
  {
    label: 'lex.ivoliq', icon: Scale,
    tabs: [
      { id: 'lex-waitlist', label: 'Pilot & Kanzlei' },
    ],
  },
  {
    label: 'Betrieb', icon: Wrench,
    tabs: [
      { id: 'support-requests', label: 'Support' },
      { id: 'email-preview', label: 'E-Mail-Vorschau' },
      { id: 'invoice-designer', label: 'Rechnungs-Designer' },
      { id: 'regulatory-radar', label: 'Regel-Radar' },
      { id: 'foreign-vendors', label: 'Auslands-Lieferanten' },
      { id: 'migration-rollback', label: 'Migration' },
    ],
  },
];

export default function Admin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [invoiceLeads, setInvoiceLeads] = useState(null);
  const [partnerLeads, setPartnerLeads] = useState(null);
  const [partnerLeadFilter, setPartnerLeadFilter] = useState('all');
  const [partnerLeadNotes, setPartnerLeadNotes] = useState({}); // id -> notes draft
  // Feb 2026 — Admin SB-Übersicht (Tree)
  const [advisorTree, setAdvisorTree] = useState(null);
  const [expandedAdvisors, setExpandedAdvisors] = useState({}); // id -> bool
  const [advisorTreeSearch, setAdvisorTreeSearch] = useState('');
  // Feb 2026 — Sprint 1: Admin-Insights (Conversion-Funnel)
  const [insights, setInsights] = useState(null);
  const [insightsWindow, setInsightsWindow] = useState(30);
  // Feb 2026 — Admin: KI-Nutzungs- & Kosten-Übersicht
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageMonth, setAiUsageMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [confirmDialog, setConfirmDialog] = useState({ 
    isOpen: false, 
    type: null, // 'grant' or 'revoke'
    email: null,
    plan: null,
    title: '',
    description: ''
  });

  useEffect(() => {
    if (user && user.email === ADMIN_EMAIL) {
      if (activeTab === 'analytics') {
        fetchAnalytics();
      } else if (activeTab === 'beta-manager') {
        fetchUsers();
      } else if (activeTab === 'invoice-leads') {
        fetchInvoiceLeads();
      } else if (activeTab === 'partner-leads') {
        fetchPartnerLeads();
      } else if (activeTab === 'sb-overview') {
        fetchAdvisorTree();
      } else if (activeTab === 'insights') {
        fetchInsights();
      } else if (activeTab === 'ai-usage') {
        fetchAiUsage();
      }
    }
  }, [user, activeTab, partnerLeadFilter, insightsWindow, aiUsageMonth]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/invoice-generator-leads');
      setInvoiceLeads(response.data);
    } catch (error) {
      console.error('Error fetching invoice leads:', error);
      setMessage('❌ Failed to load invoice leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvisorTree = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/advisors-with-clients');
      setAdvisorTree(response.data);
    } catch (error) {
      console.error('Error fetching advisor tree:', error);
      setMessage('❌ Failed to load advisor tree');
    } finally {
      setLoading(false);
    }
  };

  // SB-Pricing (Juni 2026): Admin schaltet SB-Tarif kostenlos frei (Komp)
  const handleSetAdvisorTier = async (advisorId, tier) => {
    try {
      await api.post(`/admin/advisors/${advisorId}/billing-tier`, { tier });
      toast.success(tier === 'starter'
        ? 'Komp-Freischaltung entfernt (zurück auf Starter)'
        : `Tarif „${tier === 'pro' ? 'Pro' : 'Growth'}" kostenlos freigeschaltet`);
      fetchAdvisorTree();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tarif konnte nicht gesetzt werden');
    }
  };

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/insights/upgrade-funnel?days=${insightsWindow}`);
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      setMessage('❌ Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  // Feb 2026 — KI-Kosten/Token-Nutzung pro User
  const fetchAiUsage = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/insights/ai-usage?year_month=${aiUsageMonth}`);
      setAiUsage(response.data);
    } catch (error) {
      console.error('Error fetching ai-usage:', error);
      setMessage('❌ KI-Nutzung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerLeads = async () => {
    try {
      setLoading(true);
      const params = partnerLeadFilter !== 'all' ? { status: partnerLeadFilter } : {};
      const response = await api.get('/admin/partner-leads', { params });
      setPartnerLeads(response.data);
      // Seed notes drafts
      const draft = {};
      (response.data.leads || []).forEach((l) => { draft[l.id] = l.notes || ''; });
      setPartnerLeadNotes(draft);
    } catch (error) {
      console.error('Error fetching partner leads:', error);
      setMessage('❌ Failed to load partner leads');
    } finally {
      setLoading(false);
    }
  };

  const updatePartnerLead = async (id, patch) => {
    try {
      await api.patch(`/admin/partner-leads/${id}`, patch);
      await fetchPartnerLeads();
    } catch (error) {
      console.error('Error updating partner lead:', error);
      setMessage('❌ Update fehlgeschlagen');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', { params: { search, limit: 50 } });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('❌ Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleGrantLifetime = async (userId, email, plan = 'pro') => {
    try {
      const response = await api.post(`/admin/users/${userId}/grant-lifetime`, { plan });
      setMessage(`✅ ${response.data.message}`);
      fetchUsers(); // Refresh list
      setConfirmDialog({ isOpen: false, type: null, email: null, plan: null, title: '', description: '' });
    } catch (error) {
      console.error('Grant lifetime failed:', error);
      setMessage(`❌ Failed: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleRevokeLifetime = async (userId, email) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/revoke-lifetime`);
      setMessage(`✅ ${response.data.message}`);
      fetchUsers(); // Refresh list
      setConfirmDialog({ isOpen: false, type: null, email: null, plan: null, title: '', description: '' });
    } catch (error) {
      console.error('Revoke lifetime failed:', error);
      setMessage(`❌ Failed: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleGrantClick = (userId, email, plan = 'pro') => {
    setConfirmDialog({
      isOpen: true,
      type: 'grant',
      userId: userId,
      email: email,
      plan: plan,
      title: 'Lifetime Zugang gewähren?',
      description: `Lifetime ${plan.toUpperCase()} Zugang für ${email} gewähren?`
    });
  };

  const handleRevokeClick = (userId, email) => {
    setConfirmDialog({
      isOpen: true,
      type: 'revoke',
      userId: userId,
      email: email,
      plan: null,
      title: 'Lifetime Zugang entziehen?',
      description: `Lifetime Zugang für ${email} entziehen? Benutzer wird auf Free Plan zurückgesetzt.`
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'grant') {
      handleGrantLifetime(confirmDialog.userId, confirmDialog.email, confirmDialog.plan);
    } else if (confirmDialog.type === 'revoke') {
      handleRevokeLifetime(confirmDialog.userId, confirmDialog.email);
    }
  };

  // Check if user is admin
  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-lg text-gray-600">Platform management and analytics</p>
        </div>
        {/* Sep 2026 — Branding-Testmail an die eigene Admin-Adresse */}
        <button
          type="button"
          data-testid="admin-test-email-btn"
          onClick={async () => {
            try {
              const r = await api.post('/admin/test-email');
              toast.success(`Testmail gesendet an ${r.data.sent_to}`);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Testmail fehlgeschlagen');
            }
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        >
          <Mail className="h-4 w-4" />
          Test-Mail senden
        </button>
      </div>

      {/* Gruppierte Navigation (Sep 2026): thematische Gruppen, umbruchfähige Pills */}
      <div className="space-y-3" data-testid="admin-nav">
        {ADMIN_NAV_GROUPS.map((group) => (
          <div key={group.label} className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 w-36 shrink-0">
              <group.icon className="h-3.5 w-3.5" />
              {group.label}
            </span>
            {group.tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeTab === tab.id
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="border-b border-gray-200" />

      {/* E-Mail-Template-Vorschau (Sep 2026) */}
      {activeTab === 'email-preview' && <EmailPreviewAdminPanel />}
      {activeTab === 'invoice-designer' && <InvoiceDesignerPanel />}
      {activeTab === 'regulatory-radar' && (
        <div className="max-w-5xl"><RegulatoryRadarPanel /></div>
      )}
      {activeTab === 'usage' && (
        <div className="max-w-5xl"><AdminUsagePanel /></div>
      )}

      {/* Migration-Rollback Tab (Juni 2026 — Phase 6 Support-Werkzeug) */}
      {activeTab === 'migration-rollback' && (
        <MigrationRollbackAdminPanel />
      )}

      {/* Support Requests Tab (Juni 2026 — In-App-Support-Anfragen) */}
      {activeTab === 'support-requests' && (
        <SupportRequestsAdminPanel />
      )}

      {/* Foreign Vendors Tab (Feb 2026 — dynamic RC-Pattern-Verwaltung) */}
      {activeTab === 'foreign-vendors' && (
        <ForeignVendorsAdminPanel />
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && analytics && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Referral & Conversion Tracking</h2>
          
          {/* Invoice Generator Stats */}
          {analytics.invoiceGeneratorStats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  📄 Rechnungsgenerator Conversions
                </CardTitle>
                <CardDescription>Signups über den öffentlichen Rechnungsgenerator</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{analytics.invoiceGeneratorStats.total}</div>
                    <div className="text-sm text-gray-600">Gesamt Signups</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-800">{analytics.invoiceGeneratorStats.byRegion?.de || 0}</div>
                    <div className="text-sm text-gray-600">🇩🇪 Deutschland</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-800">{analytics.invoiceGeneratorStats.byRegion?.at || 0}</div>
                    <div className="text-sm text-gray-600">🇦🇹 Österreich</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-800">{analytics.invoiceGeneratorStats.byRegion?.ch || 0}</div>
                    <div className="text-sm text-gray-600">🇨🇭 Schweiz</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* All Referral Sources */}
          {analytics.referralStats && analytics.referralStats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📈 Alle Referral Sources</CardTitle>
                <CardDescription>Top 20 Traffic-Quellen für Registrierungen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4">Source</th>
                        <th className="text-right py-2 px-4">Signups</th>
                        <th className="text-right py-2 px-4">Anteil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.referralStats.map((item, index) => {
                        const totalReferrals = analytics.referralStats.reduce((acc, i) => acc + i.count, 0);
                        const percentage = ((item.count / totalReferrals) * 100).toFixed(1);
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-4 font-medium">{item.source}</td>
                            <td className="py-2 px-4 text-right">{item.count}</td>
                            <td className="py-2 px-4 text-right text-gray-500">{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          
          {(!analytics.referralStats || analytics.referralStats.length === 0) && (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <p>Noch keine Referral-Daten vorhanden.</p>
                <p className="text-sm mt-2">Sobald Nutzer sich über Referral-Links registrieren, erscheinen hier die Statistiken.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Invoice Generator Leads Tab */}
      {activeTab === 'invoice-leads' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">📧 Invoice Generator Leads</h2>
          <p className="text-gray-600">E-Mail-Adressen von Nutzern, die den öffentlichen Rechnungsgenerator verwendet haben.</p>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Lade Leads...</p>
            </div>
          ) : invoiceLeads ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{invoiceLeads.stats?.total || 0}</div>
                    <div className="text-sm text-gray-600">Gesamt Leads</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{invoiceLeads.stats?.today || 0}</div>
                    <div className="text-sm text-gray-600">Heute</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{invoiceLeads.stats?.thisWeek || 0}</div>
                    <div className="text-sm text-gray-600">Diese Woche</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{invoiceLeads.stats?.marketingConsent || 0}</div>
                    <div className="text-sm text-gray-600">Marketing Opt-in</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs text-gray-500 mb-1">Nach Region:</div>
                    <div className="flex gap-2 text-sm">
                      <span className="bg-gray-100 px-2 py-1 rounded">🇩🇪 {invoiceLeads.stats?.byRegion?.de || 0}</span>
                      <span className="bg-gray-100 px-2 py-1 rounded">🇦🇹 {invoiceLeads.stats?.byRegion?.at || 0}</span>
                      <span className="bg-gray-100 px-2 py-1 rounded">🇨🇭 {invoiceLeads.stats?.byRegion?.ch || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Leads Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Neueste Leads
                  </CardTitle>
                  <CardDescription>Die letzten 100 erfassten E-Mail-Adressen</CardDescription>
                </CardHeader>
                <CardContent>
                  {invoiceLeads.leads && invoiceLeads.leads.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-3 px-4">E-Mail</th>
                            <th className="text-center py-3 px-4">Region</th>
                            <th className="text-center py-3 px-4">Marketing</th>
                            <th className="text-center py-3 px-4">E-Mail gesendet</th>
                            <th className="text-right py-3 px-4">Datum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceLeads.leads.map((lead, index) => (
                            <tr key={lead.id || index} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{lead.email}</td>
                              <td className="py-3 px-4 text-center">
                                {lead.region === 'de' && '🇩🇪'}
                                {lead.region === 'at' && '🇦🇹'}
                                {lead.region === 'ch' && '🇨🇭'}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {lead.consent_marketing ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {lead.invoice_sent ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-amber-500">⏳</span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right text-gray-500">
                                {new Date(lead.created_at).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Noch keine Leads erfasst.</p>
                      <p className="text-sm mt-1">Sobald Nutzer den Rechnungsgenerator verwenden, erscheinen hier die Leads.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <p>Fehler beim Laden der Leads.</p>
                <button 
                  onClick={fetchInvoiceLeads}
                  className="mt-2 text-emerald-600 hover:underline"
                >
                  Erneut versuchen
                </button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Partner Leads Tab (SB-Portal Contact Form) */}
      {activeTab === 'partner-leads' && (
        <div className="space-y-6" data-testid="admin-partner-leads-panel">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🤝 Partner-Leads (Steuerberater)</h2>
              <p className="text-gray-600">Anfragen vom SB-Portal-Kontaktformular. Status pflegen & Notizen festhalten.</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Filter:</label>
              <select
                value={partnerLeadFilter}
                onChange={(e) => setPartnerLeadFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                data-testid="partner-leads-filter"
              >
                <option value="all">Alle</option>
                <option value="new">Neu</option>
                <option value="contacted">Kontaktiert</option>
                <option value="won">Gewonnen</option>
                <option value="rejected">Abgelehnt</option>
              </select>
              <button
                onClick={fetchPartnerLeads}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Aktualisieren
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Lade Partner-Leads...</p>
            </div>
          ) : partnerLeads ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{partnerLeads.stats?.total || 0}</div>
                    <div className="text-sm text-gray-600">Gesamt</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{partnerLeads.stats?.today || 0}</div>
                    <div className="text-sm text-gray-600">Heute</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{partnerLeads.stats?.this_week || 0}</div>
                    <div className="text-sm text-gray-600">Diese Woche</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{partnerLeads.stats?.by_status?.new || 0}</div>
                    <div className="text-sm text-gray-600">Neu</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{partnerLeads.stats?.by_status?.won || 0}</div>
                    <div className="text-sm text-gray-600">Gewonnen</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-gray-600">{partnerLeads.stats?.by_status?.contacted || 0}</div>
                    <div className="text-sm text-gray-600">Kontaktiert</div>
                  </CardContent>
                </Card>
              </div>

              {/* Leads List */}
              <div className="space-y-4">
                {(partnerLeads.leads || []).length === 0 ? (
                  <Card>
                    <CardContent className="py-10 text-center text-gray-500">
                      <Handshake className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Noch keine Partner-Leads.</p>
                      <p className="text-sm mt-1">Sobald Steuerberater das Formular nutzen, erscheinen sie hier.</p>
                    </CardContent>
                  </Card>
                ) : (
                  partnerLeads.leads.map((lead) => {
                    const currentStatus = lead.status || 'new';
                    const statusBadge = {
                      new: 'bg-amber-100 text-amber-800',
                      contacted: 'bg-blue-100 text-blue-800',
                      won: 'bg-emerald-100 text-emerald-800',
                      rejected: 'bg-gray-100 text-gray-600',
                    }[currentStatus];
                    const statusLabel = { new: 'Neu', contacted: 'Kontaktiert', won: 'Gewonnen', rejected: 'Abgelehnt' }[currentStatus];
                    return (
                      <Card key={lead.id} data-testid={`partner-lead-${lead.id}`}>
                        <CardContent className="pt-5">
                          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-bold text-gray-900">{lead.name}</h3>
                                {lead.firm && <span className="text-sm text-gray-500">· {lead.firm}</span>}
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${statusBadge}`}>{statusLabel}</span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1 flex items-center gap-4 flex-wrap">
                                <a href={`mailto:${lead.email}`} className="text-emerald-600 hover:underline">{lead.email}</a>
                                {lead.phone && <span>📞 {lead.phone}</span>}
                                {lead.mandants_count && <span>👥 {lead.mandants_count} Mandanten</span>}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 text-right">
                              {new Date(lead.created_at).toLocaleString('de-DE', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </div>
                          </div>

                          <div className="bg-gray-50 border border-gray-100 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap mb-4">
                            {lead.message}
                          </div>

                          <div className="grid md:grid-cols-3 gap-3">
                            <div className="md:col-span-2">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">Interne Notizen</label>
                              <textarea
                                value={partnerLeadNotes[lead.id] ?? ''}
                                onChange={(e) => setPartnerLeadNotes({ ...partnerLeadNotes, [lead.id]: e.target.value })}
                                onBlur={(e) => {
                                  const newVal = e.target.value;
                                  if (newVal !== (lead.notes || '')) {
                                    updatePartnerLead(lead.id, { notes: newVal });
                                  }
                                }}
                                rows={2}
                                placeholder="Gesprächsnotizen, nächste Schritte …"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                data-testid={`partner-lead-notes-${lead.id}`}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">Status</label>
                              <div className="flex flex-wrap gap-1">
                                {[
                                  { value: 'new', label: 'Neu', active: 'bg-amber-600 text-white border-amber-600' },
                                  { value: 'contacted', label: 'Kontaktiert', active: 'bg-blue-600 text-white border-blue-600' },
                                  { value: 'won', label: 'Gewonnen', active: 'bg-emerald-600 text-white border-emerald-600' },
                                  { value: 'rejected', label: 'Abgelehnt', active: 'bg-gray-600 text-white border-gray-600' },
                                ].map((s) => (
                                  <button
                                    key={s.value}
                                    onClick={() => updatePartnerLead(lead.id, { status: s.value })}
                                    disabled={currentStatus === s.value}
                                    className={`text-xs px-2.5 py-1 rounded border font-medium transition-colors ${
                                      currentStatus === s.value
                                        ? `${s.active} cursor-default`
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                    data-testid={`partner-lead-status-${lead.id}-${s.value}`}
                                  >
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                              {lead.status_updated_at && (
                                <p className="text-[10px] text-gray-400 mt-1.5">
                                  Zuletzt: {new Date(lead.status_updated_at).toLocaleString('de-DE')}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <p>Fehler beim Laden der Partner-Leads.</p>
                <button
                  onClick={fetchPartnerLeads}
                  className="mt-2 text-emerald-600 hover:underline"
                >
                  Erneut versuchen
                </button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* SB-Übersicht Tab — Hierarchischer Tree (Feb 2026) */}
      {activeTab === 'sb-billing' && <PlatformBillingPanel />}

      {activeTab === 'sb-overview' && (
        <div className="space-y-6" data-testid="admin-sb-overview-panel">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="h-6 w-6 text-emerald-600" />
                Steuerberater & Mandanten
              </h2>
              <p className="text-gray-600 mt-1">
                Hierarchische Übersicht aller verbundenen Steuerberater und ihrer aktiven Mandanten.
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-2xl">
                <Shield className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  DSGVO: Diese Daten sind ausschließlich für Plattform-Administration und Support einsehbar.
                  Jeder Zugriff sollte protokolliert sein.
                </span>
              </div>
            </div>
            <button
              onClick={fetchAdvisorTree}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
              data-testid="sb-overview-refresh"
            >
              Aktualisieren
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Lade SB-Übersicht...</p>
            </div>
          ) : advisorTree ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600" data-testid="sb-stat-total-advisors">
                      {advisorTree.stats?.total_advisors || 0}
                    </div>
                    <div className="text-sm text-gray-600">Steuerberater (gesamt)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-emerald-700">
                      {advisorTree.stats?.verified_advisors || 0}
                    </div>
                    <div className="text-sm text-gray-600">verifiziert</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {advisorTree.stats?.advisors_with_clients || 0}
                    </div>
                    <div className="text-sm text-gray-600">mit Mandanten</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-purple-600" data-testid="sb-stat-total-relationships">
                      {advisorTree.stats?.total_relationships || 0}
                    </div>
                    <div className="text-sm text-gray-600">Mandanten-Verbindungen</div>
                  </CardContent>
                </Card>
              </div>

              {/* Search */}
              <div className="relative max-w-md">
                <input
                  type="text"
                  value={advisorTreeSearch}
                  onChange={(e) => setAdvisorTreeSearch(e.target.value)}
                  placeholder="Suche nach SB-Email, Name, Kanzlei oder Mandant…"
                  className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  data-testid="sb-overview-search"
                />
              </div>

              {/* Advisor List */}
              <div className="space-y-3">
                {(() => {
                  const q = advisorTreeSearch.trim().toLowerCase();
                  const filtered = (advisorTree.advisors || []).filter((adv) => {
                    if (!q) return true;
                    if ((adv.name || '').toLowerCase().includes(q)) return true;
                    if ((adv.email || '').toLowerCase().includes(q)) return true;
                    if ((adv.company_name || '').toLowerCase().includes(q)) return true;
                    if ((adv.clients || []).some((c) =>
                      (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
                    )) return true;
                    return false;
                  });

                  if (filtered.length === 0) {
                    return (
                      <Card>
                        <CardContent className="py-10 text-center text-gray-500">
                          <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{q ? 'Keine Treffer für deine Suche.' : 'Noch keine Steuerberater registriert.'}</p>
                        </CardContent>
                      </Card>
                    );
                  }

                  return filtered.map((adv) => {
                    const isOpen = !!expandedAdvisors[adv.id];
                    const planBadgeColor = (plan) => {
                      const p = (plan || 'free').toLowerCase();
                      if (p.startsWith('pro')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
                      if (p.startsWith('basic')) return 'bg-blue-100 text-blue-800 border-blue-200';
                      if (p === 'lifetime') return 'bg-amber-100 text-amber-800 border-amber-200';
                      return 'bg-gray-100 text-gray-700 border-gray-200';
                    };

                    return (
                      <Card key={adv.id} data-testid={`sb-row-${adv.id}`}>
                        <button
                          type="button"
                          onClick={() => setExpandedAdvisors({ ...expandedAdvisors, [adv.id]: !isOpen })}
                          className="w-full text-left"
                          data-testid={`sb-row-toggle-${adv.id}`}
                        >
                          <CardContent className="pt-4 pb-4">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="text-emerald-600">
                                  {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                                </div>
                                <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                                  <Building2 className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-gray-900 truncate">
                                      {adv.company_name || adv.name || '—'}
                                    </span>
                                    {adv.email_verified ? (
                                      <span className="text-xs text-emerald-600 font-medium">✓ verifiziert</span>
                                    ) : (
                                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" /> nicht verifiziert
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500 mt-0.5 truncate">
                                    {adv.name && <span>{adv.name} · </span>}
                                    <a href={`mailto:${adv.email}`} className="hover:text-emerald-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                                      {adv.email}
                                    </a>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-sm flex-shrink-0">
                                {/* SB-Pricing (Juni 2026): Tarif-Badge + Komp-Freischaltung */}
                                <div className="flex flex-col items-end gap-1" onClick={(e) => e.stopPropagation()}>
                                  <span
                                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                                      adv.billing_status === 'comp'
                                        ? 'bg-violet-100 text-violet-800 border-violet-200'
                                        : adv.billing_status === 'active'
                                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                          : 'bg-gray-100 text-gray-600 border-gray-200'
                                    }`}
                                    data-testid={`sb-tier-badge-${adv.id}`}
                                  >
                                    {(adv.billing_tier || 'starter').toUpperCase()}
                                    {adv.billing_status === 'comp' && ' · KOMP'}
                                  </span>
                                  <select
                                    value={adv.billing_status === 'comp' ? adv.billing_tier : 'starter'}
                                    onChange={(e) => handleSetAdvisorTier(adv.id, e.target.value)}
                                    disabled={adv.billing_status === 'active'}
                                    className="text-[11px] border border-gray-200 rounded-md px-1.5 py-0.5 bg-white text-gray-600 disabled:opacity-50"
                                    title={adv.billing_status === 'active' ? 'SB hat aktives Stripe-Abo' : 'Tarif kostenlos freischalten'}
                                    data-testid={`sb-tier-select-${adv.id}`}
                                  >
                                    <option value="starter">Kein Komp</option>
                                    <option value="growth">Growth gratis</option>
                                    <option value="pro">Pro gratis</option>
                                  </select>
                                </div>
                                <div className="text-center">
                                  <div className="text-xl font-bold text-gray-900">{adv.active_clients}</div>
                                  <div className="text-xs text-gray-500">Mandanten</div>
                                </div>
                                <div className="text-xs text-gray-400 hidden md:block">
                                  {adv.created_at && (
                                    <>seit {new Date(adv.created_at).toLocaleDateString('de-DE')}</>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </button>

                        {isOpen && (
                          <div className="border-t bg-gray-50 px-6 py-4">
                            {(adv.clients || []).length === 0 ? (
                              <div className="text-sm text-gray-500 italic py-3">
                                Dieser SB hat aktuell keine aktiven Mandanten.
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                      <th className="text-left py-2 px-2 font-semibold">Mandant</th>
                                      <th className="text-left py-2 px-2 font-semibold">E-Mail</th>
                                      <th className="text-center py-2 px-2 font-semibold">Plan</th>
                                      <th className="text-center py-2 px-2 font-semibold">Land</th>
                                      <th className="text-center py-2 px-2 font-semibold">Vollmacht</th>
                                      <th className="text-right py-2 px-2 font-semibold">Verbunden seit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {adv.clients.map((c) => (
                                      <tr key={c.id} className="border-b border-gray-100 hover:bg-white" data-testid={`sb-client-${c.id}`}>
                                        <td className="py-2 px-2">
                                          <div className="font-medium text-gray-900">{c.name || '—'}</div>
                                          {c.tax_office_name && (
                                            <div className="text-xs text-gray-500">FA: {c.tax_office_name}</div>
                                          )}
                                        </td>
                                        <td className="py-2 px-2">
                                          <a href={`mailto:${c.email}`} className="text-emerald-600 hover:underline">
                                            {c.email}
                                          </a>
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${planBadgeColor(c.plan)}`}>
                                            {(c.plan || 'free').toUpperCase()}
                                          </span>
                                          {c.has_lifetime_access && (
                                            <span className="ml-1 text-xs text-amber-600" title="Lifetime Access">🎁</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                          {c.country === 'DE' && '🇩🇪'}
                                          {c.country === 'AT' && '🇦🇹'}
                                          {c.country === 'CH' && '🇨🇭'}
                                          {!['DE', 'AT', 'CH'].includes(c.country) && (c.country || '—')}
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                          {c.permissions?.edit ? (
                                            <span className="text-emerald-600 font-medium" title="SB darf bearbeiten">✓ erteilt</span>
                                          ) : (
                                            <span className="text-gray-400" title="Nur Leserecht">– nur lesen</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 text-right text-gray-500 text-xs">
                                          {c.relationship_started_at && new Date(c.relationship_started_at).toLocaleDateString('de-DE')}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    );
                  });
                })()}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <p>Fehler beim Laden der SB-Übersicht.</p>
                <button onClick={fetchAdvisorTree} className="mt-2 text-emerald-600 hover:underline">
                  Erneut versuchen
                </button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Conversion-Insights Tab — Feb 2026 Sprint 1 */}
      {activeTab === 'lex-waitlist' && (
        <div className="space-y-6" data-testid="admin-lex-waitlist-tab">
          <LexWaitlistPanel />
          <KanzleiTeamPanel />
        </div>
      )}

      {/* Userübersicht — Aug 2026 */}
      {activeTab === 'users' && (
        <div className="space-y-6" data-testid="admin-users-tab">
          <AdminUserOverviewPanel />
        </div>
      )}

      {activeTab === 'insights' && (
        <div className="space-y-6" data-testid="admin-insights-panel">
          <ConversionFunnelPanel />
          <div className="border-t border-gray-200 pt-6 flex items-start justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
                Conversion-Insights
              </h2>
              <p className="text-gray-600 mt-1">
                Wie oft Free-User auf Upgrade-Paywalls treffen und klicken — pro Source, pro Plan.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={insightsWindow}
                onChange={(e) => setInsightsWindow(parseInt(e.target.value, 10))}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
                data-testid="insights-window-select"
              >
                <option value={7}>letzte 7 Tage</option>
                <option value={30}>letzte 30 Tage</option>
                <option value={90}>letzte 90 Tage</option>
                <option value={365}>letztes Jahr</option>
              </select>
              <button
                onClick={fetchInsights}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
                data-testid="insights-refresh"
              >
                Aktualisieren
              </button>
            </div>
          </div>

          {loading && !insights ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Lade Insights...</p>
            </div>
          ) : insights ? (
            <>
              {/* Top KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600" data-testid="insights-total-views">
                      {insights.totals?.views ?? 0}
                    </div>
                    <div className="text-sm text-gray-600">Paywall-Views</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-blue-600" data-testid="insights-total-clicks">
                      {insights.totals?.clicks ?? 0}
                    </div>
                    <div className="text-sm text-gray-600">Upgrade-Klicks</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-amber-600" data-testid="insights-conversion-rate">
                      {insights.totals?.conversion_rate_pct ?? 0}%
                    </div>
                    <div className="text-sm text-gray-600">Click-Through-Rate</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {insights.totals?.unique_users ?? 0}
                    </div>
                    <div className="text-sm text-gray-600">Unique-Users</div>
                  </CardContent>
                </Card>
              </div>

              {/* By Source */}
              <Card>
                <CardHeader>
                  <CardTitle>Pro Quelle (welche Paywall konvertiert?)</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  {insights.by_source?.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-4">Noch keine Tracking-Daten in diesem Zeitraum.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 uppercase tracking-wider border-b">
                          <th className="text-left py-2 px-2 font-semibold">Source</th>
                          <th className="text-right py-2 px-2 font-semibold">Views</th>
                          <th className="text-right py-2 px-2 font-semibold">Klicks</th>
                          <th className="text-right py-2 px-2 font-semibold">CTR</th>
                          <th className="text-right py-2 px-2 font-semibold">Unique Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insights.by_source.map((src) => (
                          <tr key={src.source} className="border-b hover:bg-gray-50" data-testid={`insights-source-${src.source}`}>
                            <td className="py-2 px-2 font-medium text-gray-900">{src.source}</td>
                            <td className="py-2 px-2 text-right">{src.views}</td>
                            <td className="py-2 px-2 text-right">{src.clicks}</td>
                            <td className="py-2 px-2 text-right">
                              <span className={`font-semibold ${src.conversion_rate_pct >= 10 ? 'text-emerald-600' : src.conversion_rate_pct >= 5 ? 'text-amber-600' : 'text-gray-600'}`}>
                                {src.conversion_rate_pct}%
                              </span>
                            </td>
                            <td className="py-2 px-2 text-right text-gray-600">{src.unique_users}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* By Plan */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Pro User-Plan (wer trifft auf Paywalls?)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {insights.by_plan_at_view?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic py-2">Keine Daten.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-500 uppercase tracking-wider border-b">
                            <th className="text-left py-2 px-2">Plan</th>
                            <th className="text-right py-2 px-2">Views</th>
                            <th className="text-right py-2 px-2">Klicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {insights.by_plan_at_view.map((p) => (
                            <tr key={p.user_plan} className="border-b">
                              <td className="py-2 px-2 font-medium">{p.user_plan}</td>
                              <td className="py-2 px-2 text-right">{p.views}</td>
                              <td className="py-2 px-2 text-right">{p.clicks}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Klick-Ziele (welcher Plan wird geklickt?)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {insights.click_targets?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic py-2">Keine Klicks im Zeitraum.</p>
                    ) : (
                      <ul className="space-y-2">
                        {insights.click_targets.map((t) => (
                          <li key={t.target_plan} className="flex justify-between text-sm border-b pb-2">
                            <span className="font-medium">{t.target_plan}</span>
                            <span className="text-emerald-600 font-bold">{t.count}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card><CardContent className="py-8 text-center text-gray-500">Keine Daten.</CardContent></Card>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="space-y-8">

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalUsers}</div>
            <p className="text-xs text-gray-500 mt-1">Registered accounts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalProjects}</div>
            <p className="text-xs text-gray-500 mt-1">Active projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalCustomers}</div>
            <p className="text-xs text-gray-500 mt-1">Customer records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <DollarSign className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalInvoices}</div>
            <p className="text-xs text-gray-500 mt-1">Invoices created</p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
            <CardDescription>Users by subscription plan</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics.planDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {analytics.planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Activity</CardTitle>
            <CardDescription>Activity metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Time Entries</span>
                <span className="text-lg font-semibold">{analytics.totalTimeEntries}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Hours Tracked</span>
                <span className="text-lg font-semibold">{analytics.totalHoursTracked?.toFixed(1) || 0}h</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active Users (with data)</span>
                <span className="text-lg font-semibold">{analytics.activeUsers}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tour Completion Rate</span>
                <span className="text-lg font-semibold">
                  {analytics.tourCompletionRate ? `${(analytics.tourCompletionRate * 100).toFixed(1)}%` : '0%'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Registrations Over Time */}
      {analytics.userRegistrationsOverTime && analytics.userRegistrationsOverTime.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>User Registrations</CardTitle>
            <CardDescription>New user signups over time</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.userRegistrationsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Feature Usage by Plan */}
      {analytics.featureUsage && (
        <Card>
          <CardHeader>
            <CardTitle>Feature Usage by Plan</CardTitle>
            <CardDescription>Average resources per plan</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.featureUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="plan" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgCustomers" fill="#10b981" name="Avg Customers" />
                <Bar dataKey="avgProjects" fill="#3b82f6" name="Avg Projects" />
                <Bar dataKey="avgInvoices" fill="#f59e0b" name="Avg Invoices" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      </div>
      )}

      {/* Beta Manager Tab */}
      {activeTab === 'beta-manager' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>🎁 Beta Tester Management</CardTitle>
              <CardDescription>Grant or revoke lifetime access to beta testers</CardDescription>
            </CardHeader>
            <CardContent>
              {message && (
                <div className={`mb-4 p-4 rounded-lg ${message.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {message}
                </div>
              )}

              {/* Search */}
              <form onSubmit={handleSearch} className="flex gap-4 mb-6">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or name..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </form>

              {/* User List */}
              {!loading && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Current Plan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Verified</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{u.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{u.name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              u.plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                              u.plan === 'basic' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.plan.toUpperCase()}
                              {u.stripe_subscription_id?.includes('lifetime_beta') && ' 🎁'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {u.email_verified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-gray-400">Not verified</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {u.stripe_subscription_id?.includes('lifetime_beta') ? (
                                <button
                                  onClick={() => handleRevokeClick(u.id, u.email)}
                                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold hover:bg-red-200"
                                >
                                  Revoke Lifetime
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleGrantClick(u.id, u.email, 'basic')}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold hover:bg-blue-200"
                                  >
                                    Grant Basic
                                  </button>
                                  <button
                                    onClick={() => handleGrantClick(u.id, u.email, 'pro')}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold hover:bg-purple-200"
                                  >
                                    Grant Pro
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {users.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No users found. Try searching or loading all users.
                    </div>
                  )}

                  <div className="mt-6 text-sm text-gray-600">
                    <p><strong>Total users:</strong> {users.length}</p>
                    <p className="mt-2">
                      <strong>Legend:</strong> 🎁 = Beta Tester with Lifetime Access
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feb 2026 — KI-Kosten / Token-Nutzung pro User */}
      {activeTab === 'ai-usage' && (
        <div className="space-y-6" data-testid="admin-ai-usage-content">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-2xl font-bold text-gray-900">🤖 KI-Nutzung & Kosten</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Monat:</label>
              <input
                type="month"
                value={aiUsageMonth}
                onChange={(e) => setAiUsageMonth(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm"
                data-testid="ai-usage-month-picker"
              />
              <button
                onClick={fetchAiUsage}
                className="text-sm text-emerald-700 hover:text-emerald-900 underline ml-2"
                data-testid="ai-usage-refresh"
              >
                Aktualisieren
              </button>
            </div>
          </div>

          {!aiUsage ? (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-400">
              Lade Daten…
            </div>
          ) : (
            <>
              {/* Totals-Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Gesamt-Kosten</div>
                  <div className="text-2xl font-bold text-rose-600 mt-1" data-testid="ai-usage-total-cost">
                    €{aiUsage.totals.cost_eur.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">Schätzung (Anthropic-Preise)</div>
                </div>
                <div className="bg-white border rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">KI-Calls</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{aiUsage.totals.calls.toLocaleString('de-DE')}</div>
                  <div className="text-xs text-gray-400 mt-0.5">Belege + Bankauszüge</div>
                </div>
                <div className="bg-white border rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Tokens (in/out)</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {(aiUsage.totals.total_tokens / 1000).toFixed(0)}k
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {(aiUsage.totals.input_tokens / 1000).toFixed(0)}k in · {(aiUsage.totals.output_tokens / 1000).toFixed(0)}k out
                  </div>
                </div>
                <div className="bg-white border rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Aktive User</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{aiUsage.by_user.length}</div>
                  <div className="text-xs text-gray-400 mt-0.5">mit ≥1 KI-Call</div>
                </div>
              </div>

              {/* By Service + By Model */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-xl p-4">
                  <h3 className="font-semibold mb-3 text-gray-900">Pro Service</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase text-gray-500 border-b">
                        <th className="text-left py-2">Service</th>
                        <th className="text-right py-2">Calls</th>
                        <th className="text-right py-2">Tokens</th>
                        <th className="text-right py-2">Kosten</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsage.by_service.map((s) => (
                        <tr key={s.service} className="border-b last:border-0">
                          <td className="py-2">
                            {s.service === 'receipt_extraction' && '🧾 Belege'}
                            {s.service === 'bank_extraction' && '🏦 Bankauszüge'}
                            {s.service === 'migration_extraction' && '🚚 Umzug (Migration)'}
                            {!['receipt_extraction', 'bank_extraction', 'migration_extraction'].includes(s.service) && s.service}
                          </td>
                          <td className="text-right py-2">{s.calls}</td>
                          <td className="text-right py-2 text-gray-500">{(s.total_tokens / 1000).toFixed(1)}k</td>
                          <td className="text-right py-2 font-semibold">€{s.cost_eur.toFixed(2)}</td>
                        </tr>
                      ))}
                      {aiUsage.by_service.length === 0 && (
                        <tr><td colSpan={4} className="py-4 text-center text-gray-400">Keine Daten</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white border rounded-xl p-4">
                  <h3 className="font-semibold mb-3 text-gray-900">Pro Modell</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase text-gray-500 border-b">
                        <th className="text-left py-2">Modell</th>
                        <th className="text-right py-2">Calls</th>
                        <th className="text-right py-2">Tokens</th>
                        <th className="text-right py-2">Kosten</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsage.by_model.map((m) => (
                        <tr key={m.model} className="border-b last:border-0">
                          <td className="py-2 font-mono text-xs">{m.model}</td>
                          <td className="text-right py-2">{m.calls}</td>
                          <td className="text-right py-2 text-gray-500">{(m.total_tokens / 1000).toFixed(1)}k</td>
                          <td className="text-right py-2 font-semibold">€{m.cost_eur.toFixed(2)}</td>
                        </tr>
                      ))}
                      {aiUsage.by_model.length === 0 && (
                        <tr><td colSpan={4} className="py-4 text-center text-gray-400">Keine Daten</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pro User */}
              <div className="bg-white border rounded-xl p-4">
                <h3 className="font-semibold mb-3 text-gray-900">Pro User (Top-Spender)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ai-usage-user-table">
                    <thead>
                      <tr className="text-xs uppercase text-gray-500 border-b">
                        <th className="text-left py-2">E-Mail / Name</th>
                        <th className="text-left py-2">Plan</th>
                        <th className="text-right py-2">Calls</th>
                        <th className="text-right py-2">Input</th>
                        <th className="text-right py-2">Output</th>
                        <th className="text-right py-2">Total</th>
                        <th className="text-right py-2">Kosten</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsage.by_user.map((u) => (
                        <tr key={u.user_id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-2">
                            <div className="font-medium">{u.email}</div>
                            {u.name && <div className="text-xs text-gray-500">{u.name}</div>}
                          </td>
                          <td className="py-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              u.plan === 'lifetime' ? 'bg-purple-100 text-purple-700'
                                : u.plan?.includes('pro') ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {u.plan}
                            </span>
                          </td>
                          <td className="text-right py-2">{u.calls}</td>
                          <td className="text-right py-2 text-gray-500">{u.input_tokens.toLocaleString('de-DE')}</td>
                          <td className="text-right py-2 text-gray-500">{u.output_tokens.toLocaleString('de-DE')}</td>
                          <td className="text-right py-2 font-medium">{u.total_tokens.toLocaleString('de-DE')}</td>
                          <td className="text-right py-2 font-bold text-rose-700">€{u.cost_eur.toFixed(3)}</td>
                        </tr>
                      ))}
                      {aiUsage.by_user.length === 0 && (
                        <tr><td colSpan={7} className="py-6 text-center text-gray-400">Keine KI-Calls in diesem Monat</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, type: null, email: null, plan: null, title: '', description: '' })}
        onConfirm={handleConfirmAction}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.type === 'grant' ? 'Gewähren' : 'Entziehen'}
        cancelText="Abbrechen"
        variant={confirmDialog.type === 'revoke' ? 'destructive' : 'default'}
      />
    </div>
  );
}
